/**
 * HemodynamicsCard — iter1057 — MAP, пулсово налягане, приблизителен дебит
 * и диастолният прозорец за коронарния кръвоток. Нула нови входове.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "@/App";
import { Activity, AlertTriangle, ChevronDown, Loader2 } from "lucide-react";

export const HemodynamicsCard = ({ isDark, refreshKey }) => {
  const { API } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/cardio/hemodynamics`)
      .then(({ data }) => setData(data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [API, refreshKey]);

  const card = isDark ? "bg-slate-900/60 border-slate-800" : "bg-white border-slate-200";
  const box = isDark ? "bg-slate-800/50" : "bg-slate-100";

  if (loading) {
    return <div className={`p-5 rounded-2xl border ${card} flex justify-center`} data-testid="hemodynamics-loading">
      <Loader2 className="w-5 h-5 animate-spin text-red-500" /></div>;
  }
  if (!data) return null;

  return (
    <div className={`p-5 rounded-2xl border-2 ${card}`} data-testid="hemodynamics-card">
      <div className="flex items-center gap-2 mb-1">
        <Activity className="w-4 h-4 text-red-500" />
        <h2 className="font-bold text-sm">Хемодинамика</h2>
        {data.category?.label_bg && (
          <span className="ml-auto text-[11px] px-2 py-1 rounded-md font-bold text-white"
                style={{ background: data.category.color }} data-testid="bp-category">
            {data.category.label_bg}
          </span>
        )}
      </div>
      <p className="text-[11px] opacity-70 mb-3">
        Всичко тук се смята от налягането и пулса, които вече въвеждаш — без нов вход.
      </p>

      {!data.has_data && (
        <p className="text-xs opacity-80" data-testid="hemodynamics-empty">
          Впиши едно налягане и един пулс в покой по-долу и целият слой се отваря.
        </p>
      )}

      {data.flags?.map((f, i) => (
        <div key={i} className={`p-3 rounded-xl mb-2 flex gap-2 ${isDark ? "bg-rose-900/30 border border-rose-500/40" : "bg-rose-50 border border-rose-200"}`}
             data-testid={`hemodynamics-flag-${i}`}>
          <AlertTriangle className="w-4 h-4 text-rose-500 shrink-0 mt-0.5" />
          <div>
            <div className="text-xs font-bold">{f.label_bg}</div>
            <div className="text-[11px] opacity-85 mt-0.5 leading-relaxed">{f.text_bg}</div>
          </div>
        </div>
      ))}

      {data.metrics?.length > 0 && (
        <div className="space-y-1.5" data-testid="hemodynamics-metrics">
          {data.metrics.map((m) => (
            <div key={m.key} className={`rounded-xl overflow-hidden ${box}`}>
              <button onClick={() => setOpen(open === m.key ? null : m.key)}
                      className="w-full text-left p-2.5 flex items-center gap-2"
                      data-testid={`hemo-metric-${m.key}`}>
                <span className="text-[11px] font-bold flex-1 min-w-0">
                  {m.label_bg}
                  {m.approx && <span className="opacity-50 font-normal"> · ориентир</span>}
                </span>
                <span className={`text-sm font-bold tabular-nums ${m.ok ? "text-emerald-500" : "text-amber-500"}`}>
                  {m.value}{m.unit_bg ? ` ${m.unit_bg}` : ""}
                </span>
                <span className="text-[10px] opacity-50 shrink-0">({m.ref_bg})</span>
                <ChevronDown className={`w-3 h-3 opacity-50 shrink-0 transition-transform ${open === m.key ? "rotate-180" : ""}`} />
              </button>
              {open === m.key && (
                <div className="px-2.5 pb-2.5 text-[11px] leading-relaxed opacity-85" data-testid={`hemo-metric-text-${m.key}`}>
                  {m.band_bg && <div className="font-bold mb-1">{m.band_bg}</div>}
                  {m.text_bg}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {data.patterns?.map((p) => (
        <div key={p.id} className={`mt-2 p-3 rounded-xl ${isDark ? "bg-amber-900/20 border border-amber-500/30" : "bg-amber-50 border border-amber-200"}`}
             data-testid={`bp-pattern-${p.id}`}>
          <div className="text-xs font-bold">{p.label_bg}</div>
          <div className="text-[11px] opacity-85 mt-1 leading-relaxed">{p.text_bg}</div>
        </div>
      ))}

      {data.cycle_note_bg && (
        <div className={`mt-3 p-3 rounded-xl ${isDark ? "bg-red-900/20 border border-red-500/30" : "bg-red-50 border border-red-200"}`}
             data-testid="diastolic-window">
          <div className="text-xs font-bold mb-1">Диастолен прозорец — кога се храни сърцето</div>
          <div className="grid grid-cols-3 gap-2 my-2 text-center">
            {[["Цикъл", `${data.cycle.cycle_sec} сек`], ["Систола", `${data.cycle.systole_sec} сек`],
              ["Диастола", `${data.cycle.diastole_sec} сек`]].map(([l, v]) => (
              <div key={l} className={`p-1.5 rounded-lg ${box}`}>
                <div className="text-[9px] opacity-60">{l}</div>
                <div className="text-[11px] font-bold tabular-nums">{v}</div>
              </div>
            ))}
          </div>
          <div className="text-[11px] opacity-85 leading-relaxed">{data.cycle_note_bg}</div>
        </div>
      )}

      {data.ratio_bg && <p className="text-[10px] opacity-60 mt-2" data-testid="bp-ratio">{data.ratio_bg}</p>}

      {data.gravity?.text_bg && (
        <p className="text-[10px] opacity-60 mt-1" data-testid="gravity-note">
          <span className="font-bold">Позиция на ръката: </span>{data.gravity.text_bg}
        </p>
      )}

      {data.missing_bg?.length > 0 && (
        <p className="text-[10px] opacity-70 mt-2" data-testid="hemodynamics-missing">
          За пълен слой липсва: {data.missing_bg.join("; ")}.
        </p>
      )}
    </div>
  );
};

export default HemodynamicsCard;
